import { DropdownSkeleton, MultiSelect } from '@carbon/react';
import type { FieldEditorProps } from '@pos/admin-ui';
import { localized, useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { hoApi } from '../api';

interface SaleTypeChoice {
  code: string;
  label: string;
}

/** The tenant's active sale types, by code (an order list's filter names them by code). */
async function listSaleTypes(): Promise<SaleTypeChoice[]> {
  const { data, error } = await hoApi.GET('/api/v1/menu/config/sale-types', { params: { query: { pageSize: 100 } } });
  if (!data) throw error;
  return data.items
    .filter((s) => s.isActive)
    .map((s) => ({
      code: s.code,
      label: `${localized(s.nameI18n as Partial<Record<string, string>>, s.name)} (${s.code})`,
    }));
}

/** The filter as an object, whether it came as JSON text or already parsed. */
function parse(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
  if (typeof value !== 'string' || !value.trim()) return {};
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
  } catch {
    return {};
  }
}

/**
 * An order list section's filter (FR-CFG-04) as a choice of sale types, instead of JSON: the POS lists the open orders of the chosen
 * sale types (none: every sale type). Saved as `{"saleTypes": ["TAKEAWAY", "PICKUP"]}`, keeping any other keys. Only order lists
 * have one; for other sections nothing is shown.
 */
export function SectionListFilterField({ field, value, values, invalidText, onChange }: FieldEditorProps) {
  const { t } = useTranslation();
  const saleTypes = useQuery({ queryKey: ['ho', 'sale-type-choices'], queryFn: listSaleTypes });
  if (values.sectionType !== 'order_list') return null;
  if (saleTypes.isLoading) return <DropdownSkeleton />;

  const filter = parse(value);
  const chosen = new Set(
    Array.isArray(filter.saleTypes) ? filter.saleTypes.filter((s): s is string => typeof s === 'string') : [],
  );
  const items = saleTypes.data ?? [];
  // A code no longer among the active sale types stays chosen (and shown by its code) until it is taken out.
  const all = [
    ...items,
    ...[...chosen].filter((c) => !items.some((i) => i.code === c)).map((code) => ({ code, label: code })),
  ];
  return (
    <MultiSelect<SaleTypeChoice>
      id={`field-${field.key}`}
      titleText={t('posLayouts.saleTypes')}
      label={t('posLayouts.saleTypesPlaceholder')}
      helperText={t('posLayouts.saleTypesHelp')}
      items={all}
      itemToString={(i) => i?.label ?? ''}
      selectedItems={all.filter((i) => chosen.has(i.code))}
      invalid={Boolean(invalidText)}
      invalidText={invalidText}
      selectionFeedback="fixed"
      onChange={({ selectedItems }) => {
        const next: Record<string, unknown> = { ...filter, saleTypes: (selectedItems ?? []).map((i) => i.code) };
        if ((next.saleTypes as string[]).length === 0) delete next.saleTypes;
        onChange(JSON.stringify(next));
      }}
    />
  );
}
